import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Folder, FileText, Download, ChevronRight, Search, ArrowLeft,
  BookMarked, Home as HomeIcon,
} from "lucide-react";
import { toast } from "sonner";

/**
 * R146 — Resident-facing Document Library.
 *
 * Read-only tree of folders + files the resident is permitted to see
 * (visibility ∈ {'all','residents'}). Backing endpoint:
 *   GET  /api/documents/library?property_id=…&folder_id=…
 *   GET  /api/documents/{did}/download
 */
function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function mimeLabel(mime = "") {
  if (mime.includes("pdf")) return "PDF";
  if (mime.includes("word")) return "DOC";
  if (mime.includes("sheet") || mime.includes("excel")) return "XLS";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "PPT";
  if (mime.startsWith("image/")) return "IMG";
  if (mime.startsWith("text/")) return "TXT";
  return "FILE";
}

const MIME_STYLES = {
  PDF: "border-rose-500/40 text-rose-300 bg-rose-500/[0.06]",
  DOC: "border-sky-500/40 text-sky-300 bg-sky-500/[0.06]",
  XLS: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.06]",
  PPT: "border-amber-500/40 text-amber-300 bg-amber-500/[0.06]",
  IMG: "border-purple-500/40 text-purple-300 bg-purple-500/[0.06]",
  TXT: "border-zinc-500/40 text-zinc-300 bg-zinc-500/[0.06]",
  FILE: "border-white/10 text-white/60",
};

export default function ResidentDocumentsPage() {
  const [current, setCurrent] = useState(null);   // null = root
  const [payload, setPayload] = useState({ folders: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [downloading, setDownloading] = useState(null);

  // R147c — debounce server-side search so we hit the endpoint at rest.
  useEffect(() => {
    const t = setTimeout(() => setActiveQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // In search mode, backend spans every folder + honours ACL grants.
      const params = {};
      if (activeQuery) params.q = activeQuery;
      else if (current) params.folder_id = current.id;
      const { data } = await api.get("/documents/library", { params });
      setPayload(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load documents");
    } finally { setLoading(false); }
  }, [current, activeQuery]);

  useEffect(() => { load(); }, [load]);

  // In search mode we show *all* matching folders (spanning parents);
  // otherwise show folders at the current level.
  const visibleFolders = useMemo(() => {
    if (activeQuery) {
      const q = activeQuery.toLowerCase();
      return (payload.folders || []).filter((f) =>
        (f.name || "").toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q));
    }
    return (payload.folders || []).filter((f) =>
      (f.parent_id || null) === (current?.id || null)
    );
  }, [payload.folders, current, activeQuery]);

  // Server already filtered by q; just render what we got.
  const visibleFiles = payload.documents || [];

  const folderById = useMemo(
    () => Object.fromEntries((payload.folders || []).map((f) => [f.id, f])),
    [payload.folders],
  );

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await fetch(`${API}/documents/${doc.id}/download`,
                                { credentials: "include" });
      if (!res.ok) throw new Error(`http ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.filename || doc.name || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally { setDownloading(null); }
  };

  return (
    <div data-testid="resident-documents-page">
      <PageHeader
        eyebrow="Community"
        title="Documents"
        subtitle="Governing documents, meeting minutes, financials, and forms — anything your board publishes shows up here."
      />

      {/* Breadcrumb + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border transition ${
            !current && !activeQuery
              ? "border-orange-500/40 bg-orange-500/[0.08] text-orange-300"
              : "border-white/10 text-white/60 hover:bg-white/[0.03]"
          }`}
          onClick={() => { setCurrent(null); setQuery(""); }}
          data-testid="docs-crumb-root"
        >
          <HomeIcon className="h-3.5 w-3.5" /> All folders
        </button>
        {current && !activeQuery && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-white/30" />
            <div className="text-xs px-2.5 py-1.5 rounded-md border border-orange-500/40 bg-orange-500/[0.08] text-orange-300"
                 data-testid="docs-crumb-current">
              {current.name}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCurrent(null)}
                    className="text-white/60 hover:text-white h-7 px-2"
                    data-testid="docs-back-btn">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
          </>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search across every folder…"
                 className="pl-8 h-8 w-64"
                 data-testid="docs-search" />
        </div>
      </div>

      {/* Search banner */}
      {activeQuery && (
        <div className="mb-4 rounded-md border border-orange-500/30 bg-orange-500/[0.06] px-3 py-2 flex items-center gap-2 text-xs text-orange-200"
             data-testid="docs-search-banner">
          <Search className="h-3.5 w-3.5" />
          Showing every folder + file you can see that matches
          <span className="font-semibold">&ldquo;{activeQuery}&rdquo;</span>.
          <button onClick={() => setQuery("")} className="ml-auto text-orange-200/70 hover:text-orange-100">
            Clear
          </button>
        </div>
      )}

      {/* Folders grid */}
      {visibleFolders.length > 0 && (
        <>
          <div className="overline mb-2 flex items-center gap-2">
            <Folder className="h-3.5 w-3.5 text-orange-400" />
            Folders <span className="text-white/40 tabular-nums">({visibleFolders.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {visibleFolders.map((f) => (
              <Card key={f.id}
                    className="border-white/10 bg-card hover:border-orange-500/30 cursor-pointer transition p-4"
                    onClick={() => { setCurrent(f); setQuery(""); }}
                    data-testid={`docs-folder-${f.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-orange-500/[0.10] border border-orange-500/20 flex items-center justify-center shrink-0">
                    <BookMarked className="h-4 w-4 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{f.name}</div>
                    {f.description && (
                      <div className="text-[11px] text-white/50 line-clamp-2 mt-0.5"
                           data-testid={`docs-folder-desc-${f.id}`}>
                        {f.description}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-white/30 shrink-0 mt-1.5" />
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Files list */}
      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <FileText className="h-4 w-4 text-orange-400" />
          <div className="text-sm font-semibold">
            {activeQuery
              ? `Matching files`
              : current ? `Files in ${current.name}` : "Files at the root"}
          </div>
          <Badge variant="outline" className="border-white/10 text-white/60 tabular-nums">
            {visibleFiles.length}
          </Badge>
        </div>
        {loading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-9 rounded bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : visibleFiles.length === 0 ? (
          <div className="p-8 text-sm text-white/50 text-center"
               data-testid="docs-empty">
            {activeQuery
              ? `No files match "${activeQuery}".`
              : current
                ? `No files in this folder yet.`
                : `No files at the top level. Try opening one of the folders above.`}
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {visibleFiles.map((d) => {
              const kind = mimeLabel(d.mime);
              const home = d.folder_id ? folderById[d.folder_id] : null;
              return (
                <li key={d.id}
                    className="px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-white/[0.02]"
                    data-testid={`docs-file-${d.id}`}>
                  <Badge variant="outline"
                         className={`${MIME_STYLES[kind] || MIME_STYLES.FILE} font-mono text-[10px] shrink-0`}>
                    {kind}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{d.name}</div>
                    <div className="text-[11px] text-white/40 truncate flex items-center gap-1.5">
                      {activeQuery && home && (
                        <button onClick={() => { setCurrent(home); setQuery(""); }}
                                className="inline-flex items-center gap-1 text-orange-300 hover:text-orange-200"
                                data-testid={`docs-file-folder-${d.id}`}>
                          <Folder className="h-3 w-3" /> {home.name}
                        </button>
                      )}
                      {activeQuery && home && <span className="text-white/20">·</span>}
                      <span className="truncate">{d.description || d.filename || "—"}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-white/40 tabular-nums shrink-0">
                    {fmtBytes(d.size)}
                  </div>
                  <Button size="sm" variant="outline"
                          disabled={downloading === d.id}
                          onClick={() => download(d)}
                          className="border-white/10 h-8 px-2 shrink-0"
                          data-testid={`docs-download-${d.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1" />
                    {downloading === d.id ? "…" : "Download"}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
